import React, { useEffect, useCallback, memo, useMemo, useState, useRef } from 'react';
import { createSelector } from 'reselect';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useDispatch, useSelector } from '@atlassian/jira-react-redux/src/index.tsx';

import {
	commentRepliesSelector,
	childCommentsPageInfoSelector,
	loadingStageSelector,
	isReplyingToCommentSelector,
	commentRepliesWithCollapseStateSelector,
	commentCollapseInfoSelector,
} from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { COMMENTS } from '@atlassian/jira-issue-view-common-constants/src/activity-items.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { getPermalinkStatus } from '@atlassian/jira-platform-issue-permalinks/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	fetchNewerChildCommentsRequest,
	fetchOlderChildCommentsRequest,
	setCollapseInfo,
	type CollapseActionPayload,
} from '@atlassian/jira-issue-view-store/src/actions/comment-actions.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	ActionButton,
	CollapseActionType,
	SAVE_CHILD_COMMENT_LOADING_PREFIX,
} from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import Comment from '../../comment-view/index.tsx';
import ReplyCommentWrapper from '../../comment-reply-wrapper/index.tsx';
import ShowMoreRepliesButton from '../../comment-show-more-replies/index.tsx';
import CollapseRepliesContainer from '../../comment-collapse-replies-container/index.tsx';

export type CommentRepliesComponentProps = {
	commentSessionId?: string | null;
	fullIssueUrl: string | null;
	commentId: string;
};

const memoizedReplies = createSelector(
	(commentId: string, state: State) => commentRepliesSelector(commentId)(state),
	(result) => result,
);

const memoizedRepliesWithCollapseState = createSelector(
	(commentId: string, state: State) => commentRepliesWithCollapseStateSelector(commentId)(state),
	(result) => result,
);

const memoizedCommentCollapseInfoSelector = createSelector(
	(commentId: string, state: State) => commentCollapseInfoSelector(commentId)(state),
	(result) => result,
);

const memoizedPagesInfo = createSelector(
	(commentId: string, state: State) => childCommentsPageInfoSelector(commentId)(state),
	(result) => result,
);
const memoizedReplyEditor = createSelector(
	(commentId: string, state: State) => isReplyingToCommentSelector(commentId)(state),
	(result) => result,
);

// Memoized permalink status to avoid recalculation
const getMemoizedPermalinkStatus = () => getPermalinkStatus(COMMENTS);

export const CommentRepliesOld = (props: CommentRepliesComponentProps) => {
	const { fullIssueUrl, commentSessionId, commentId } = props;
	const dispatch = useDispatch();
	const isReplyingToComment = useSelector((state: State) => memoizedReplyEditor(commentId, state));
	const replies = useSelector((state: State) => memoizedReplies(commentId, state));
	const childCommentsPageInfo = useSelector((state: State) => memoizedPagesInfo(commentId, state));
	const loadingStage = useSelector((state: State) => loadingStageSelector(state));

	const getMoreOlderComments = useCallback(() => {
		dispatch(fetchOlderChildCommentsRequest(commentId));
	}, [dispatch, commentId]);
	const getMoreNewerComments = useCallback(() => {
		dispatch(fetchNewerChildCommentsRequest(commentId));
	}, [dispatch, commentId]);

	const previousRepliesLength = usePrevious(replies?.length);

	useEffect(() => {
		if (childCommentsPageInfo) {
			const repliesLength = replies?.length;

			if (previousRepliesLength !== repliesLength && (!replies || repliesLength === 0)) {
				if (childCommentsPageInfo.hasNextPage) {
					getMoreOlderComments();
				} else if (childCommentsPageInfo.hasPreviousPage) {
					getMoreNewerComments();
				}
			}
		}
	}, [
		replies,
		childCommentsPageInfo,
		previousRepliesLength,
		getMoreNewerComments,
		getMoreOlderComments,
	]);

	if (!replies || replies.length === 0) return null;

	const { hasPermalink, permalinkId } = getPermalinkStatus(COMMENTS);

	const showOlderComments = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
			olderComments: true,
		});
		getMoreOlderComments();
	};

	const showNewerComments = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
			newerComments: true,
		});
		getMoreNewerComments();
	};

	const hasReplies = replies && replies.length > 0;

	const isNewChildCommentSaving =
		loadingStage === `${SAVE_CHILD_COMMENT_LOADING_PREFIX}${commentId}`;

	const isLatestChildCommentNotVisible = childCommentsPageInfo?.hasPreviousPage;

	if (isNewChildCommentSaving && isLatestChildCommentNotVisible) {
		return (
			<ShowMoreRepliesButton
				onClickHandler={noop}
				commentId={commentId}
				type={ActionButton.PREVIOUS}
				forceShowLoadingState
			/>
		);
	}

	return (
		<>
			{childCommentsPageInfo?.hasNextPage && (
				<ShowMoreRepliesButton
					onClickHandler={showOlderComments}
					commentId={commentId}
					type={ActionButton.PREVIOUS}
				/>
			)}
			{hasReplies &&
				replies.map((childCommentId, index) => (
					<ReplyCommentWrapper
						key={childCommentId}
						isFirst={index === 0 && !childCommentsPageInfo?.hasNextPage}
						isLast={
							index === replies.length - 1 &&
							!isReplyingToComment &&
							!childCommentsPageInfo?.hasPreviousPage
						}
					>
						<Comment
							commentId={childCommentId}
							fullIssueUrl={fullIssueUrl}
							isHighlighted={hasPermalink && childCommentId === permalinkId}
							commentSessionId={commentSessionId}
							isThreadedCommentsEnabled
						/>
					</ReplyCommentWrapper>
				))}
			{childCommentsPageInfo?.hasPreviousPage && (
				<ShowMoreRepliesButton
					onClickHandler={showNewerComments}
					commentId={commentId}
					type={ActionButton.NEXT}
					isReplyingToComment={isReplyingToComment}
				/>
			)}
		</>
	);
};

export const CommentRepliesNew = memo((props: CommentRepliesComponentProps) => {
	const { fullIssueUrl, commentSessionId, commentId } = props;
	const dispatch = useDispatch();

	// Optimized selectors with stable references
	const isReplyingToComment = useSelector((state: State) => memoizedReplyEditor(commentId, state));
	const repliesAll = useSelector((state: State) => memoizedReplies(commentId, state));
	const childCommentsPageInfo = useSelector((state: State) => memoizedPagesInfo(commentId, state));
	const loadingStage = useSelector((state: State) => loadingStageSelector(state));
	const replies = useSelector((state: State) => memoizedRepliesWithCollapseState(commentId, state));
	const commentCollapseInfo = useSelector((state: State) =>
		memoizedCommentCollapseInfoSelector(commentId, state),
	);

	const [collapseActionType, setCollapseActionType] = useState<
		CollapseActionPayload['collapseActionType']
	>(CollapseActionType.INITIAL);

	const hasInitializedRef = useRef(false);

	const getMoreOlderComments = useCallback(() => {
		dispatch(fetchOlderChildCommentsRequest(commentId));
	}, [dispatch, commentId]);
	const getMoreNewerComments = useCallback(() => {
		dispatch(fetchNewerChildCommentsRequest(commentId));
	}, [dispatch, commentId]);

	const previousRepliesLength = usePrevious(replies?.length);
	const previousRepliesAllLength = usePrevious(repliesAll?.length);

	useEffect(() => {
		if (childCommentsPageInfo) {
			const repliesLength = replies?.length;

			if (previousRepliesLength !== repliesLength && (!replies || repliesLength === 0)) {
				if (childCommentsPageInfo.hasNextPage) {
					getMoreOlderComments();
				} else if (childCommentsPageInfo.hasPreviousPage) {
					getMoreNewerComments();
				}
			}
		}
	}, [
		replies,
		childCommentsPageInfo,
		previousRepliesLength,
		getMoreNewerComments,
		getMoreOlderComments,
	]);

	const { hasPermalink, permalinkId } = useMemo(() => getMemoizedPermalinkStatus(), []);

	// Initialize collapse info on first render
	useEffect(() => {
		if (hasInitializedRef.current || !repliesAll?.length) {
			return;
		}

		dispatch(
			setCollapseInfo({
				commentId,
				collapseActionType: CollapseActionType.INITIAL,
				originalTopMarker: repliesAll[0],
				originalBottomMarker: repliesAll[repliesAll.length - 1],
				currentTopMarker: repliesAll[0],
				currentBottomMarker: repliesAll[repliesAll.length - 1],
			}),
		);
		hasInitializedRef.current = true;
	}, [repliesAll, dispatch, commentId]);

	// Update collapse info when replies change
	useEffect(() => {
		if (!repliesAll?.length || previousRepliesAllLength === repliesAll.length) {
			return;
		}

		const currentTopMarker = repliesAll[0];
		const currentBottomMarker = repliesAll[repliesAll.length - 1];

		// Update top marker if we have more older comments
		if (collapseActionType === CollapseActionType.SHOW_MORE_OLDER) {
			dispatch(
				setCollapseInfo({
					commentId,
					collapseActionType,
					currentTopMarker,
					currentBottomMarker,
				}),
			);
		}

		// Update bottom marker if we have more newer comments
		if (collapseActionType === CollapseActionType.SHOW_MORE_NEWER) {
			dispatch(
				setCollapseInfo({
					commentId,
					collapseActionType,
					currentBottomMarker,
				}),
			);
		}
	}, [
		repliesAll,
		previousRepliesAllLength,
		commentCollapseInfo?.currentTopMarker,
		commentCollapseInfo?.currentBottomMarker,
		dispatch,
		commentId,
		commentCollapseInfo?.collapseActionType,
		collapseActionType,
	]);

	const showOlderComments = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
				olderComments: true,
			});

			setCollapseActionType(CollapseActionType.SHOW_MORE_OLDER);

			if (repliesAll?.length && commentCollapseInfo?.currentTopMarker !== repliesAll[0]) {
				dispatch(
					setCollapseInfo({
						commentId,
						collapseActionType: CollapseActionType.SHOW_MORE_OLDER,
						currentTopMarker: repliesAll[0],
					}),
				);
			} else {
				getMoreOlderComments();
			}
		},
		[commentCollapseInfo?.currentTopMarker, commentId, dispatch, getMoreOlderComments, repliesAll],
	);

	const showNewerComments = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
				newerComments: true,
			});

			setCollapseActionType(CollapseActionType.SHOW_MORE_NEWER);

			if (
				repliesAll?.length &&
				commentCollapseInfo?.currentBottomMarker !== repliesAll[repliesAll.length - 1]
			) {
				dispatch(
					setCollapseInfo({
						commentId,
						collapseActionType: CollapseActionType.SHOW_MORE_NEWER,
					}),
				);
			} else {
				getMoreNewerComments();
			}
		},
		[
			commentCollapseInfo?.currentBottomMarker,
			commentId,
			dispatch,
			getMoreNewerComments,
			repliesAll,
		],
	);

	const hidePreviousComments = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'showLessReplies', {
				olderComments: true,
			});

			setCollapseActionType(CollapseActionType.SHOW_LESS_OLDER);

			dispatch(
				setCollapseInfo({
					commentId,
					collapseActionType: CollapseActionType.SHOW_LESS_OLDER,
				}),
			);
		},
		[commentId, dispatch],
	);

	const hideNewerComments = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'showLessReplies', {
				newerComments: true,
			});

			setCollapseActionType(CollapseActionType.SHOW_LESS_NEWER);

			dispatch(
				setCollapseInfo({
					commentId,
					collapseActionType: CollapseActionType.SHOW_LESS_NEWER,
				}),
			);
		},
		[commentId, dispatch],
	);

	const hasReplies = replies && replies.length > 0;

	const isNewChildCommentSaving =
		loadingStage === `${SAVE_CHILD_COMMENT_LOADING_PREFIX}${commentId}`;

	const isLatestChildCommentNotVisible = childCommentsPageInfo?.hasPreviousPage;

	// Memoized comment render function with optimized dependencies
	const renderComment = useCallback(
		(childCommentId: string, index: number) => {
			// Determine if this is the last visible reply in the current view
			const isLastVisibleReply = index === (replies?.length ?? 0) - 1;

			// Check if we're not currently replying to a comment
			const isNotReplying = !isReplyingToComment;

			// Check if there are no more previous pages to load
			const hasNoPreviousPages = !childCommentsPageInfo?.hasPreviousPage;

			// Check if the current view represents the complete threaded state with collapse/expand option visible
			const isCompleteThreadState = !(
				childCommentsPageInfo?.hasPreviousPage ||
				repliesAll?.[repliesAll.length - 1] !== replies?.[replies.length - 1] ||
				commentCollapseInfo?.currentBottomMarker !== commentCollapseInfo?.originalBottomMarker
			);

			// An item is last when it's the last visible reply, we're not replying,
			// there are no previous pages, and the thread state is complete

			const isLastWithoutCollapseEnabled =
				isLastVisibleReply && isNotReplying && hasNoPreviousPages;

			const isLast =
				!(isLastWithoutCollapseEnabled && isCompleteThreadState) &&
				expVal('jira_collapse_threaded_comment', 'isEnabled', false)
					? false
					: isLastWithoutCollapseEnabled;

			return (
				<ReplyCommentWrapper
					key={childCommentId}
					isFirst={index === 0 && !childCommentsPageInfo?.hasNextPage}
					isLast={isLast}
				>
					<Comment
						commentId={childCommentId}
						fullIssueUrl={fullIssueUrl}
						isHighlighted={hasPermalink && childCommentId === permalinkId}
						commentSessionId={commentSessionId}
						isThreadedCommentsEnabled
					/>
				</ReplyCommentWrapper>
			);
		},
		[
			replies,
			isReplyingToComment,
			childCommentsPageInfo?.hasPreviousPage,
			childCommentsPageInfo?.hasNextPage,
			repliesAll,
			fullIssueUrl,
			hasPermalink,
			permalinkId,
			commentSessionId,
			commentCollapseInfo?.currentBottomMarker,
			commentCollapseInfo?.originalBottomMarker,
		],
	);

	const repliesList: React.JSX.Element[] | null = useMemo(
		() =>
			hasReplies && replies
				? replies.map((childCommentId, index) => renderComment(childCommentId, index))
				: null,
		[hasReplies, replies, renderComment],
	);

	// Memoized collapse container props to prevent unnecessary re-renders
	const topCollapseProps = useMemo(
		() => ({
			commentId,
			type: ActionButton.PREVIOUS,
			showMoreButton: childCommentsPageInfo?.hasNextPage || repliesAll?.[0] !== replies?.[0],
			onShowMoreClickHandler: showOlderComments,
			onShowLessClickHandler: hidePreviousComments,
			showLessButton:
				commentCollapseInfo?.currentTopMarker !== commentCollapseInfo?.originalTopMarker,
		}),
		[
			commentId,
			childCommentsPageInfo?.hasNextPage,
			repliesAll,
			replies,
			showOlderComments,
			hidePreviousComments,
			commentCollapseInfo?.currentTopMarker,
			commentCollapseInfo?.originalTopMarker,
		],
	);

	const bottomCollapseProps = useMemo(
		() => ({
			commentId,

			type: ActionButton.NEXT,
			isReplyingToComment,
			showMoreButton:
				childCommentsPageInfo?.hasPreviousPage ||
				repliesAll?.[repliesAll.length - 1] !== replies?.[replies.length - 1],
			onShowMoreClickHandler: showNewerComments,
			showLessButton:
				commentCollapseInfo?.currentBottomMarker !== commentCollapseInfo?.originalBottomMarker,
			onShowLessClickHandler: hideNewerComments,
		}),
		[
			commentId,
			isReplyingToComment,
			childCommentsPageInfo?.hasPreviousPage,
			repliesAll,
			replies,
			showNewerComments,
			commentCollapseInfo?.currentBottomMarker,
			commentCollapseInfo?.originalBottomMarker,
			hideNewerComments,
		],
	);

	// Early return for empty state
	if (!repliesList || repliesList.length === 0) return null;

	// Early return for loading state
	if (isNewChildCommentSaving && isLatestChildCommentNotVisible) {
		return (
			<CollapseRepliesContainer
				onShowMoreClickHandler={noop}
				onShowLessClickHandler={noop}
				commentId={commentId}
				type={ActionButton.PREVIOUS}
				showLessButton={false}
				showMoreButton
				forceShowLoadingState
			/>
		);
	}
	return (
		<>
			<CollapseRepliesContainer {...topCollapseProps} />
			{repliesList}
			<CollapseRepliesContainer {...bottomCollapseProps} />
		</>
	);
});

const CommentReplies = componentWithFG(
	'jira_collapse_threaded_comment_gate',
	CommentRepliesNew,
	CommentRepliesOld,
);

export default CommentReplies;
